import { Box, Button, Stack } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import SalaryForkListLayout from './SalaryForkListLayout';
import { Link } from 'react-router-dom';
import { useSalaryForksUrl } from '../routing';
import { useAuthorization } from 'framework/authorization';
import useSalaryForkService, {
  SALARY_FORKS_CREATE,
  SALARY_FORKS_READ_HISTORY,
  SALARY_FORKS_UPDATE,
} from '../useSalaryForkService';
import { DEFAULT_ENTITY_AUTHOR } from 'domain/shared/core/constants';
import { EntityHeader, ShowSection, ShowPageLayout } from 'framework/ui/layout';
import { FormShowTemplate, BELARUS } from 'framework/form';
import { COMPENSATION_CURRENCY_TYPES } from 'domain/shared/newVacancy';
import { useShowContext } from 'framework/show';
import { RECRUITING_CONTEXT } from 'domain/recruiting/RecruitingContext';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { cloneDeep } from 'lodash';

const SalaryForkPage = () => {
  const { data } = useShowContext();
  const { id } = data || {};
  const { getHistoryUrl } = useSalaryForksUrl();
  const { isGranted } = useAuthorization();
  const canViewHistory = isGranted(SALARY_FORKS_READ_HISTORY, 'Recruiting');

  const historyUrl = getHistoryUrl(id || '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-hj-suppress>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Stack direction="row" spacing={2}>
          {canViewHistory && (
            <Button variant="outlined" startIcon={<HistoryIcon />} component={Link} to={historyUrl} disabled={!id}>
              View History
            </Button>
          )}
        </Stack>
      </Box>

      <SalaryForkListLayout />
    </Box>
  );
};

const SalaryForkPageLayout = () => {
  const { isGranted } = useAuthorization();
  const { updateSalaryFork, createSalaryFork, searchSalaryForksForSpec, getCreateValidationSchema } =
    useSalaryForkService();
  const { addMessage } = useMessageService();
  const canCreate = isGranted(SALARY_FORKS_CREATE, RECRUITING_CONTEXT);
  const canUpdate = isGranted(SALARY_FORKS_UPDATE, RECRUITING_CONTEXT);

  const handleSubmit = async (values: any) => {
    if (!canCreate) {
      addMessage(new ErrorMessage('You do not have permission to create salary forks'));
      return {};
    }
    if (!canUpdate) {
      addMessage(new ErrorMessage('You do not have permission to update salary forks'));
      return {};
    }

    try {
      // Update forks
      if (values.id) {
        await updateSalaryFork(values, values.initialData);
      } else {
        await createSalaryFork(values);
      }

      // Get new forks with actual data
      const salaryForks = await searchSalaryForksForSpec(values.specialization, values.technology);

      return {
        country: values.country,
        currency: values.currency,
        initialData: cloneDeep(salaryForks),
        ...salaryForks,
        specialization: values.specialization,
        technology: values.technology,
      };
    } catch (error) {
      throw error;
    }
  };

  return (
    <FormShowTemplate
      getDataFunc={async () => {}}
      getValidationSchemaFunc={getCreateValidationSchema}
      afterSubmit={{
        message: 'Salary Ranges successfully updated.',
      }}
      initialData={{
        country: BELARUS.code,
        currency: COMPENSATION_CURRENCY_TYPES.USD,
      }}
      submitFormFunc={handleSubmit}
    >
      <ShowPageLayout>
        <ShowSection
          sx={{ mb: '0px' }}
          title={<EntityHeader entityTitle="Salary Ranges" defaultAuthor={DEFAULT_ENTITY_AUTHOR} color="brand" />}
        >
          <SalaryForkPage />
        </ShowSection>
      </ShowPageLayout>
    </FormShowTemplate>
  );
};

export default SalaryForkPageLayout;
